import React, { useState, useEffect, useCallback } from 'react';
import { useGame } from '../contexts/GameContext';
import { t } from '../i18n/translations';
import { updateLeadScore, createScore, getScores, sendGameEmail } from '../services/api';

const EndScreen = () => {
  const { 
    language, settings, currentLead, currentScore, endType, 
    resetToAttract, isTop10, setIsTop10, finalScoreRef
  } = useGame();
  
  // Use finalScoreRef as the authoritative score source (immune to React batching issues)
  const effectiveScore = finalScoreRef.current || currentScore;
  
  const [initials, setInitials] = useState(['', '', '']);
  const [countdown, setCountdown] = useState(settings?.endscreen_timeout || 10);
  const [initialsSubmitted, setInitialsSubmitted] = useState(false);
  const [scoreRank, setScoreRank] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [scoreCountUp, setScoreCountUp] = useState(0);
  const inputRefs = [React.useRef(), React.useRef(), React.useRef()];

  const getEndText = () => {
    if (endType === 'victory') {
      return language === 'it' 
        ? 'CAMPIONE! Hai completato tutti e 3 gli stage! Passa allo stand e scopri i premi per i campioni!'
        : 'CHAMPION! You completed all 3 stages! Visit our stand and discover the prizes for champions!';
    }
    if (endType === 'gameover') {
      return language === 'it' 
        ? settings?.gameover_text_it || "FINE CORSA... PER ORA! Passa allo stand e scopri la 'Corsia Preferenziale' di Palletways."
        : settings?.gameover_text_en || "END OF THE ROAD... FOR NOW! Visit our stand and discover Palletways' 'Fast Lane'.";
    }
    return language === 'it'
      ? settings?.timeout_text_it || "CONSEGNA COMPLETATA! Sei un vero professionista della logistica."
      : settings?.timeout_text_en || "DELIVERY COMPLETED! You're a true logistics professional.";
  };

  const getScoreTitle = () => {
    const tier1 = settings?.tier1_threshold || 5000;
    const tier2 = settings?.tier2_threshold || 15000;
    if (effectiveScore <= tier1) {
      return language === 'it' ? settings?.tier1_title_it || "Apprendista Logistico" : settings?.tier1_title_en || "Logistics Apprentice";
    } else if (effectiveScore <= tier2) {
      return language === 'it' ? settings?.tier2_title_it || "Export Manager" : settings?.tier2_title_en || "Export Manager";
    }
    return language === 'it' ? settings?.tier3_title_it || "Export Manager Livello PRO!" : settings?.tier3_title_en || "Export Manager PRO Level!";
  };

  const checkTop10 = useCallback(async () => {
    const score = finalScoreRef.current;
    try {
      const scores = await getScores(10);
      if (scores.length < 10 || score > scores[scores.length - 1].score) {
        setIsTop10(true);
        const rank = scores.filter(s => s.score > score).length + 1;
        setScoreRank(rank);
      }
    } catch (error) { console.error('Error checking top 10:', error); }
  }, [finalScoreRef, setIsTop10]);

  const saveScore = useCallback(async () => {
    const score = finalScoreRef.current;
    if (currentLead && currentLead.id && currentLead.id !== 'test-user') {
      try {
        await updateLeadScore(currentLead.id, score, endType);
        sendGameEmail(currentLead.id, score, endType, language).catch(() => {});
      } catch (error) { console.error('Error updating lead score:', error); }
    }
  }, [currentLead, finalScoreRef, endType, language]);

  useEffect(() => { saveScore(); checkTop10(); }, [saveScore, checkTop10]);

  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Score count-up animation
  useEffect(() => {
    if (!showContent) return;
    const score = finalScoreRef.current;
    const duration = 1500;
    const steps = 30;
    const increment = score / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      if (step >= steps) {
        setScoreCountUp(score);
        clearInterval(timer);
      } else {
        setScoreCountUp(Math.floor(increment * step));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [showContent, finalScoreRef]);

  // Countdown timer
  useEffect(() => {
    if (initialsSubmitted || !isTop10) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) { clearInterval(timer); resetToAttract(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [initialsSubmitted, isTop10, resetToAttract]);

  useEffect(() => {
    if (!isTop10) {
      const timer = setTimeout(() => resetToAttract(), (settings?.endscreen_timeout || 10) * 1000);
      return () => clearTimeout(timer);
    }
  }, [isTop10, settings, resetToAttract]);

  const handleInitialChange = (index, value) => {
    // Take only last character, force uppercase, letters only (Android keyboard fix)
    const char = value.slice(-1).toUpperCase().replace(/[^A-Z]/g, '');
    const newInitials = [...initials];
    newInitials[index] = char;
    setInitials(newInitials);
    if (char && index < 2) inputRefs[index + 1].current?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !initials[index] && index > 0) inputRefs[index - 1].current?.focus();
  };

  const handleSubmitInitials = async () => {
    const score = finalScoreRef.current;
    const initialsStr = initials.join('');
    if (initialsStr.length === 3) {
      try {
        await createScore({ initials: initialsStr, score, lead_id: currentLead?.id });
        setInitialsSubmitted(true);
      } catch (error) { console.error('Error submitting score:', error); }
    }
  };

  const isGameOver = endType === 'gameover';
  const isVictory = endType === 'victory';
  const mainColor = isVictory ? '#FFD100' : isGameOver ? '#FF0000' : '#00FF41';
  const accentColor = '#FFD100';

  const gameBg = settings?.color_game_bg || '#000000';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden" style={{ backgroundColor: gameBg }} data-testid="end-screen">
      {/* CRT scanlines */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0px, rgba(0,0,0,0.15) 2px, transparent 2px, transparent 4px)',
        zIndex: 50,
      }} />

      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.8) 100%)',
        zIndex: 49,
      }} />

      {/* Flicker overlay */}
      <div className="absolute inset-0 pointer-events-none animate-crt-flicker" style={{ zIndex: 48 }} />

      <div className={`relative z-40 flex flex-col items-center w-full max-w-lg transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        
        {/* GAME OVER / TIME UP title */}
        <div className="mb-6">
          <h1 
            className="font-mono text-6xl sm:text-7xl lg:text-8xl font-black text-center tracking-wider"
            style={{ 
              color: mainColor,
              textShadow: `0 0 10px ${mainColor}, 0 0 30px ${mainColor}, 0 0 60px ${mainColor}40`,
              animation: 'arcadeGlow 1.5s ease-in-out infinite alternate',
            }}
            data-testid="end-title"
          >
            {isVictory
              ? (language === 'it' ? 'HAI VINTO!' : 'YOU WIN!')
              : isGameOver 
              ? (language === 'it' ? 'GAME OVER' : 'GAME OVER')
              : (language === 'it' ? 'TEMPO SCADUTO' : 'TIME UP')
            }
          </h1>
        </div>

        {/* Score counter */}
        <div className="text-center mb-2">
          <p className="font-mono text-xs tracking-[0.3em] mb-2" style={{ color: '#666' }}>
            {language === 'it' ? '- PUNTEGGIO FINALE -' : '- FINAL SCORE -'}
          </p>
          <p 
            className="font-mono text-5xl sm:text-6xl font-black tabular-nums"
            style={{ 
              color: accentColor,
              textShadow: `0 0 8px ${accentColor}80, 0 0 20px ${accentColor}40`,
            }}
            data-testid="end-score"
          >
            {scoreCountUp.toLocaleString()}
          </p>
        </div>

        {/* Score title */}
        <div className="mb-6">
          <p 
            className="font-mono text-sm text-center tracking-wider animate-blink"
            style={{ color: '#00BFFF' }}
          >
            {getScoreTitle()}
          </p>
        </div>

        {/* Divider line */}
        <div className="w-full h-px mb-4" style={{
          background: `linear-gradient(90deg, transparent, ${mainColor}60, ${mainColor}, ${mainColor}60, transparent)`,
        }} />

        {/* End text */}
        <p className="font-mono text-xs text-center leading-relaxed mb-6 px-4" style={{ color: '#8888aa' }}>
          {getEndText()}
        </p>

        {/* TOP 10 Initials */}
        {isTop10 && !initialsSubmitted && (
          <div 
            className="w-full rounded border-2 p-5 mb-4"
            style={{ 
              borderColor: accentColor,
              backgroundColor: 'rgba(255,209,0,0.05)',
              boxShadow: `0 0 15px ${accentColor}20, inset 0 0 15px ${accentColor}10`,
            }}
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="font-mono text-xs" style={{ color: accentColor }}>&#9733;</span>
              <span 
                className="font-mono text-sm font-bold tracking-wider animate-blink"
                style={{ color: accentColor }}
              >
                TOP 10 — #{scoreRank}
              </span>
              <span className="font-mono text-xs" style={{ color: accentColor }}>&#9733;</span>
            </div>
            
            <p className="font-mono text-[10px] text-center mb-4 tracking-wider" style={{ color: '#666' }}>
              {language === 'it' ? 'INSERISCI LE TUE INIZIALI' : 'ENTER YOUR INITIALS'}
            </p>
            
            <div className="flex gap-3 justify-center mb-4">
              {initials.map((char, index) => (
                <input
                  key={index}
                  ref={inputRefs[index]}
                  type="text"
                  maxLength={1}
                  value={char}
                  onChange={(e) => handleInitialChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-14 h-16 text-center font-mono text-3xl font-black border-2 rounded bg-black outline-none transition-colors focus:border-yellow-400"
                  style={{ 
                    color: accentColor,
                    borderColor: char ? accentColor : '#333',
                    textShadow: `0 0 8px ${accentColor}60`,
                    caretColor: accentColor,
                  }}
                  data-testid={`initial-input-${index}`}
                />
              ))}
            </div>
            
            <button
              onClick={handleSubmitInitials}
              disabled={initials.join('').length !== 3}
              className="w-full py-3 font-mono text-sm font-bold tracking-wider rounded transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              style={{
                backgroundColor: initials.join('').length === 3 ? accentColor : '#222',
                color: initials.join('').length === 3 ? '#000' : '#555',
                boxShadow: initials.join('').length === 3 ? `0 0 15px ${accentColor}40` : 'none',
              }}
              data-testid="submit-initials-btn"
            >
              OK
            </button>
          </div>
        )}

        {/* Countdown */}
        {(initialsSubmitted || !isTop10) && (
          <p className="font-mono text-xs tracking-wider animate-blink" style={{ color: '#555' }} data-testid="countdown">
            {language === 'it' ? 'NUOVA PARTITA TRA' : 'NEW GAME IN'} {countdown}s
          </p>
        )}

        {/* Insert coin / Skip */}
        <button
          onClick={resetToAttract}
          className="mt-6 font-mono text-xs tracking-[0.2em] transition-colors hover:opacity-100"
          style={{ color: '#444' }}
          data-testid="skip-btn"
        >
          {language === 'it' ? '[ PREMI PER CONTINUARE ]' : '[ PRESS TO CONTINUE ]'}
        </button>
      </div>

      <style>{`
        @keyframes arcadeGlow {
          0% { opacity: 0.85; filter: brightness(0.9); }
          100% { opacity: 1; filter: brightness(1.2); }
        }
        .animate-crt-flicker {
          animation: crtFlicker 0.15s infinite;
        }
        @keyframes crtFlicker {
          0%, 100% { background: transparent; }
          50% { background: rgba(255,255,255,0.01); }
        }
      `}</style>
    </div>
  );
};

export default EndScreen;
